import { Component, Prop, Element, Event, EventEmitter, Method, State, Listen, h, Host } from '@stencil/core';
import { generateId } from '../../utils/functions';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from '../../utils/bodyScrollLock';
import { KEY_CODES } from '../../utils/variables';

/**
 * Modal dialogs are full screen overlays that interrupt user interaction and demand an action. They are appropriate when user’s attention needs to be directed toward important information.
 */
@Component({
  tag: 'som-modal',
  styleUrl: 'som-modal.scss',
})
export class SomModal {
  @Element() el: HTMLElement;

  /**
   * This property will be displayed in the header of your modal.
   */
  @Prop() headline: string;

  /**
   * This property sets the aria label of the modal.
   */
  @Prop() modalAriaLabel: string;

  /**
   * This property will not be displayed on the screen but will be used to further describe the modal to screen reader users.
   */
  @Prop() description: string;

  /**
   * This boolean is used to control the width of the modal.
   */
  @Prop() size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * This boolean is used to enable closing the modal with keyboard's ESC key.
   */
  @Prop() enableKeyboardClose: boolean = true;

  /**
   * This boolean is used to enable closing the modal when clicking on the overlay outside of the modal.
   */
  @Prop() enableOverlayClose: boolean = true;

  /**
   * This boolean is used to hide the header's close button.
   */
  @Prop() hideCloseButton: boolean = false;

  /**
   * This property is used to set padding around the card
   */
  @Prop() modalPadding: string = "2";

  /**
   * @deprecated with version 4.0 release
   * This monitors whether the modal is open or closed
   */
  @State() modalHidden: boolean;

  /**
   * This event is emitted before the modal opens.
   */
  @Event() modalOpen: EventEmitter;

  /**
   * This event is emitted after the modal closes.
   */
  @Event() modalClose: EventEmitter;

  /**
   * Private Variables
   */

  modalElement;

  modalContainerEl; // Modal content container

  modalEl; // Modal element

  // Holds unique id for describe div - this is in place in case the user injects multiple modals on a page and we can ensure id uniqueness
  describedById;

  modalBody;

  modalCloseBtn;

  /**
   * This method will open the modal.
   * @returns Promise will resolve when the open animation ends.
   */
  @Method()
  open(): Promise<void> {
    return new Promise(resolve => {
      if (!this.modalElement.open) {
        // Triggers that the modal will open
        this.modalOpen.emit();

        this.modalElement.showModal();

        // Make sure scolling on body is disabled (allow scrolling only on the modal element)
        disableBodyScroll(this.modalElement);

        resolve();
      }
    });
  }

  /**
   * This method will close the modal.
   * @returns Promise will resolve when the close animation ends.
   */
  @Method()
  close(): Promise<void> {
    return new Promise(resolve => {

      if (this.modalElement.open) {

        // Enable body scrolling back
        enableBodyScroll(this.modalElement);

        // Triggers that the modal has closed
        this.modalClose.emit();

        // Close modal
        setTimeout(() => {
          this.modalElement.close();
          resolve();
        }, 0);
      } else {
        // Already closed
        resolve();
      }
    });
  }

  /**
   * This method will refresh the modal. This is useful when modal data is loaded asynchronously.
   *  @deprecated with version 4.0 release
   *  @returns Promise will resolve after the refresh.
   */
  @Method()
  refresh(): Promise<void> {
    return new Promise(resolve => {
      setTimeout(() => resolve(), 0);
    });
  }

  @Listen('keydown')
  _handleKeyDown(e) {
    switch (e.keyCode) {
      case KEY_CODES.ESC:
        if (!this.enableKeyboardClose) {
          e.preventDefault();
          e.returnValue = false;
        } else {
          this.close();
        }
        break;
      default:
        break;
    }
  }

  handleOverlayClick(e) {
    // Close modal when a click occurs only on the dialog and the enableOverlayClose is set to true
    if (e.target === this.modalElement && this.enableOverlayClose) {
      this.close();
    }
  }

  handleModalScroll(event) {
    // There is no close button so don't run this code.
    if (this.hideCloseButton) return true;

    const e = event.target;
    if (e.scrollTop) {
      this.modalCloseBtn.classList.add('scrolling');
    } else {
      this.modalCloseBtn.classList.remove('scrolling');
    }
  }

  getModalStyles() {
    const styles = ['modal'];
    if (this.size) styles.push(`modal-${this.size}`);
    return styles.join(' ');
  }

  componentWillLoad() {
    this.describedById = generateId('modal-description');
  }

  componentDidLoad() {
    this.modalElement = this.el.querySelector('dialog');

    this.modalBody = this.el.querySelector('.modal-body');

    this.modalCloseBtn = this.el.querySelector('.close-button');

    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.modalElement.addEventListener('click', this.handleOverlayClick);

    this.handleModalScroll = this.handleModalScroll.bind(this);
    this.modalElement.addEventListener('scroll', this.handleModalScroll);
  }

  disconnectedCallback() {
    // Make sure to reenable body scroll in case it was previously deactivated
    clearAllBodyScrollLocks();
  }

  render() {
    const opts: any = {
      'class': this.getModalStyles(),
    };

    if (this.modalAriaLabel) {
      opts['aria-label'] = this.modalAriaLabel;
    }

    if (this.description) {
      opts['aria-describedby'] = this.describedById;
    }

    this.modalEl = (
      <dialog {...opts} ref={el => this.modalContainerEl = el}>
        {!this.hideCloseButton ? (
          <button class="close-button" onClick={() => this.close()} aria-label="Close modal">
            <span aria-hidden="true">
              <som-icon name="close" display="block"></som-icon>
            </span>
          </button>
        ) : null}
        <div class="modal-content">
          {this.description ? (
            <div id={this.describedById} class="sr-only">
              {this.description}
            </div>
          ) : null}
          <div class="modal-body">
            <div class={`modal-body-content p-${this.modalPadding}`}>
              {this.headline ? <div class="title mb-1_5">{this.headline}</div> : null }
              <slot />
            </div>
          </div>
        </div>
      </dialog>
    );

    return (
      <Host>
        {this.modalEl}
      </Host>
    );
  }
}
